<template>
  <div>
    <public-announcement />
    <div class="flex items-center justify-center min-h-screen p-4 overflow-hidden">
      <div class="relative z-10 w-100">
        <div class="flex justify-center">
          <span class="pt-10 pb-14" v-if="!$store.state.isPresenterWhiteLabel">
            <a href="https://ahaslides.com">
              <aha-logo
                width="140"
                height="24"
                viewBox="0 0 9600 1602"
              />
            </a>
          </span>
        </div>

        <!-- Form -->
        <div class="shadow-panel rounded-3xl bg-base-0 px-9 pt-10 pb-12">
          <h2 class="mb-4 text-center text-2xl">
            Sign up
          </h2>

          <h5 class="text-sm text-center mb-7 text-base-90" v-if="!referralUser">
            <p>
              Or
              <a href="#" @click="goToLogin()" class="font-bold text-link--primary">
                Log in to your account
              </a>
            </p>
          </h5>

          <div class="mb-3 text-center" v-if="inviteCodeExpired">
            <h4 class="mb-1 text-lg font-bold">
              Your invite link has expired
            </h4>
            <p>Please request your inviter for another invite.</p>
          </div>

          <div v-if="!inviteCodeExpired">
            <div v-if="!$store.state.isPresenterWhiteLabel">
              <google-button-v2 type="signup">
                Sign up with Google
              </google-button-v2>

              <div class="separator my-6">
                or
              </div>
            </div>

            <form @submit.prevent="onSubmitRegister">
              <aha-input-group class="relative mb-4">
                <span class="aha-input-prepend">
                  <aha-icon name="mail" />
                </span>

                <aha-input
                  type="email"
                  class="w-full"
                  placeholder="Your email"
                  required
                  v-model="email"
                  :disabled="inviteCode"
                />
              </aha-input-group>

              <aha-input-group class="relative mb-4">
                <span class="aha-input-prepend">
                  <aha-icon name="lock" />
                </span>

                <aha-input
                  :type="showPassword ? 'text' : 'password'"
                  class="w-full aha-input_limit-char"
                  placeholder="Your password"
                  size="md"
                  required
                  v-model="password"
                />

                <span
                  class="absolute flex items-center cursor-pointer inset-y-2 right-3"
                  @click="showPassword = !showPassword"
                >
                  <font-awesome-icon
                    class="text-base-80"
                    :icon="!showPassword ? ['fas', 'eye'] : ['fas', 'eye-slash']"
                  />
                </span>
              </aha-input-group>

              <aha-input-group class="mb-4">
                <span class="aha-input-prepend">
                  <aha-icon name="name" />
                </span>

                <aha-input
                  type="text"
                  class="w-full"
                  placeholder="Your full name"
                  required
                  v-model="name"
                  size="md"
                />
              </aha-input-group>

              <div class="mb-4 text-sm text-red-100" v-if="textError">
                <i class="fas fa-exclamation-triangle fa-fw" aria-hidden="true" />
                {{ textError }}
              </div>

              <div class="text-center form-group mb-4">
                <div class="col-xs-12">
                  <vue-recaptcha
                    :sitekey="keyRecaptcha"
                    @verify="onVerify"
                    @error="onRecaptchaError"
                    size="invisible"
                    @expired="onExpired"
                    :load-recaptcha-script="true"
                    recaptcha-host="www.recaptcha.net"
                    ref="recaptcha"
                    class="auth-captcha"
                  />

                  <aha-button
                    type="submit"
                    class="w-full"
                    size="lg"
                    :submitting="submitting"
                    :disabled="isDisabled"
                  >
                    Sign Up
                  </aha-button>
                </div>
              </div>

              <p
                class="text-center"
                v-if="
                  !isFromMSTeams
                    &&
                    !$store.state.isPresenterWhiteLabel
                    &&
                    !guestUserId"
              >
                <a

                  class="font-bold text-link--primary"
                  @click="handleLoginWithSso()"
                >
                  Sign up with SSO
                </a>
              </p>
            </form>
          </div>
        </div>

        <p class="text-sm text-center mt-8 px-8">
          By signing up you accept our <a href="https://ahaslides.com/terms/" target="_blank" class="primary">terms of use</a> and <a href="https://ahaslides.com/privacy/" target="_blank" class="primary">privacy policy</a>.
        </p>

        <div class="h-28" />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { authentication, app } from '@microsoft/teams-js'
import VueRecaptcha from 'vue-recaptcha'
import * as Sentry from '@sentry/vue'
import configs from '@/config'
import utils from '@/util'
import GoogleButtonV2 from '@/components-v2/common/auth/GoogleButtonV2.vue'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'

// services
import { PRESENTER_REGISTER } from '@/constant/gaCategories'
import cookie from '@/services/cookie'

// api
import UserApi from '@/api/user'

const plans = [
  'essential_annually',
  'pro_annually',
  'essential_monthly',
  'pro_monthly',
  'plus_monthly',
  'plus_annually',
  'edu_small',
  'edu_medium',
  'edu_large',
  'one_time_mini',
  'one_time_small',
  'one_time_medium',
  'one_time_large',
]

export default {
  components: {
    VueRecaptcha,
    GoogleButtonV2,
    PublicAnnouncement,
  },
  data() {
    return {
      referralUser: null,
      referralCode: null,
      email: '',
      password: '',
      checkbox_remember_me: true,
      textError: '',
      submitting: false,
      name: '',
      fromPricingPage: false,
      domain: '',
      keyRecaptcha: configs.keyRecapcha,
      tokenRecaptcha: '',
      inviteCode: '',
      inviteCodeExpired: false,
      showPassword: false,
      guestUserId: cookie.getValue('aha-guest-user'),
      signUpLanguage: '',
      isFirstSignUpSession: true,
      isFromMSTeams: false,
    }
  },
  computed: {
    saveSpinnerWheel() {
      return utils.getParamValue('spinnerwheel', window.location.href)
    },
    saveWordcloud() {
      return utils.getParamValue('wordcloud', window.location.href)
    },
    isDisabled() {
      return !(this.email || this.password || this.name)
    },
  },
  async created() {
    const pricingSource = window.location.search.indexOf('source=pricing')
    if (pricingSource !== -1) {
      this.fromPricingPage = true
    }

    await this.checkInvitation()

    const token = cookie.getValue('ahaToken')
    if (token && !this.guestUserId) {
      this.$router.push('/apps/presentations')
    }
    this.detectAndSetPageLanguage()
  },
  async mounted() {
    const { 'referral-code': referralCode } = this.$route.query

    this.domain = configs.domain
    this.referralCode = referralCode

    const isFromMSTeams = utils.getParamValue('ms_teams', window.location.href)
    this.isFromMSTeams = !!isFromMSTeams
    if (isFromMSTeams) {
      await app.initialize()
    }

    document.body.classList.remove('no-scroll')
    if (this.$store.state.isPresenterWhiteLabel) {
      this.$router.push({
        path: '/pages/login',
      })
    }

    utils.preloadOnboardingImages()
    // get referral user information
    if (referralCode) {
      try {
        const users = await UserApi.getUsers({ referralCode })
        this.referralUser = users[0] || null
      } catch {
        // do nothing
      }
    }

    utils.gaTrackEvent(
      PRESENTER_REGISTER,
      'Open sign up form',
    )
  },
  methods: {
    onVerify(response) {
      this.tokenRecaptcha = response
      utils.gaTrackEvent(
        PRESENTER_REGISTER,
        'Recaptcha verified',
      )
      this.register()
    },
    onRecaptchaError() {
      this.submitting = false
      this.textError = 'Recaptcha error. If the problem still occurs, please check your internet connection.'
    },
    onSubmitRegister() {
      if (this.submitting) return
      this.submitting = true
      utils.gaTrackEvent(
        PRESENTER_REGISTER,
        'Click Sign up button',
      )
      if (this.email.match(/qa\.ahaslides\+(.*)@gmail.com/i)) {
        this.register()
      } else {
        this.$refs.recaptcha.execute()
        utils.removeRecaptchaCover()
      }
    },
    onExpired() {
      this.$refs.recaptcha.reset()
      this.tokenRecaptcha = ''
    },

    async checkInvitation() {
      this.inviteCode = utils.getParamValue('invite-code', window.location.href)

      if (this.inviteCode) {
        try {
          const res = await this.$http.get(configs.api.getInvitation + this.inviteCode)
          const { expireAt, invitedEmail, invitedName } = res.body

          if (new Date(expireAt).getTime() < Date.now()) {
            this.inviteCodeExpired = true
          }

          this.email = invitedEmail
          this.name = invitedName
        } catch (err) {
          if (Sentry && err.status) {
            Sentry.captureException(
              `Check invitation code ${this.inviteCode}, error: ${JSON.stringify(err)}`,
            )
          }
        }
      }
    },

    goToLogin() {
      const content = utils.getParamValue('content', window.location.href)
      const source = utils.getParamValue('source', window.location.href)
      const promo = utils.getParamValue('promo', window.location.href)

      let query = {}

      if (content && source === 'pricing') {
        query = {
          content,
          source,
        }
        if (promo) {
          query.promo = promo
        }
      }
      if (this.isFromMSTeams) {
        query = {
          ms_teams: true,
        }
      }
      this.$router.push({
        path: '/pages/login',
        query,
      })
    },
    isValidSignUpForm() {
      if (this.password.length < 6) {
        this.textError = 'Password needs at least 6 characters.'
        this.$refs.recaptcha.reset()
        this.tokenRecaptcha = ''
        return false
      }

      if (!utils.validateEmail(this.email)) {
        this.textError = 'Invalid Email.'
        this.$refs.recaptcha.reset()
        this.tokenRecaptcha = ''
        return false
      }

      if (this.name.length > 40) {
        this.textError = 'Your name must be no longer than 40 characters.'
        this.$refs.recaptcha.reset()
        this.tokenRecaptcha = ''
        return false
      }

      return true
    },

    handleRegisterSuccess(res, payload, asGuest = false) {
      cookie.setValue('aha-new-user', true)
      utils.removeFirstPageCookie()
      // user create from signup page and check i'm ready onboarding

      const content = utils.getParamValue('content', window.location.href)
      const promo = utils.getParamValue('promo', window.location.href)
      const redirect = utils.getParamValue('redirect', window.location.href)
      const hasPlan = plans.includes(content)

      if (asGuest) {
        this.$store.dispatch('setCurrentUserAction', res.body)
        utils.gaTrackPageView(`/apps/presentations/demo-signup-success/?email=${payload.email}&fullName=${this.name}`)
        utils.gaTrackEvent(
          PRESENTER_REGISTER,
          'Sign up',
          'Demo',
          null,
          {
            signUpLanguage: this.signUpLanguage,
            firstPage: payload.firstPage,
          },
        )
        utils.ga4Track('sign_up', {
          method: 'Demo',
        })

        cookie.deleteValue('aha-guest-user')
      } else {
        if (!this.saveSpinnerWheel && !this.saveWordcloud) {
          this.$store.state.isComeFromSignupPage = true
        }
        this.$store.dispatch('setCurrentUserAction', res.body.user)
        utils.gaTrackPageView(`/apps/presentations/app-signup-success/?email=${payload.email}&fullName=${this.name}`)
        utils.gaTrackEvent(
          PRESENTER_REGISTER,
          'Sign up',
          `${this.inviteCode ? 'With invite code' : 'Without invite code'}`,
          null,
          {
            signUpLanguage: this.signUpLanguage,
            inviteCode: this.inviteCode,
            firstPage: payload.firstPage,
          },
        )
        utils.ga4Track('sign_up', {
          method: `${this.inviteCode ? 'With invite code' : 'Without invite code'}`,
        })

        utils.setCookieToken(res.body.token)
        if (this.isFromMSTeams) {
          authentication.notifySuccess(res.body.token)
        }
        Vue.http.headers.common.Authorization = `Bearer ${res.body.token}`
      }
      window.dataLayer.push({
        event: 'signup',
      })

      if (this.fromPricingPage && hasPlan) {
        const plan = content
          .replace(/_/g, '-')
          .replace(/annually/g, 'yearly')

        let url = `${configs.presenterDomain}/apps/upgrade/${plan}`
        if (promo) {
          url += `?promo=${promo}`
        }

        window.location.href = url

        return
      }
      if (redirect && redirect !== '/apps/presentations') {
        window.location.href = redirect
        return
      }

      if (this.saveSpinnerWheel) {
        const query = {
          spinnerwheel: true,
        }
        this.$router.push({ path: '/save-presentation', query })
        return
      }

      if (this.saveWordcloud) {
        const query = {
          wordcloud: true,
        }
        this.$router.push({ path: '/save-presentation', query })
        return
      }
      this.$router.push({ name: 'Presentations' })
      this.submitting = false
      this.tokenRecaptcha = ''
    },

    handleRegisterError(err) {
      this.submitting = false
      this.$refs.recaptcha.reset()
      this.tokenRecaptcha = ''

      if (err.body && err.body.message) {
        this.textError = err.body.message
        this._setTranslateTextError(err.body.message)
        utils.gaTrackEvent(
          PRESENTER_REGISTER,
          'Sign Up Button > Submit > Failed',
          err.body.message,
        )

        return
      }
      this.textError = 'Invalid email or password. Please try again!'
      utils.gaTrackEvent(
        PRESENTER_REGISTER,
        'Sign Up Button > Submit > Invalid',
        JSON.stringify(err),
      )
    },

    _setTranslateTextError(msg) {
      if (msg === 'Email and password are required') {
        this.textError = 'Email and password are required'
      }
      if (msg === 'Email already used.') {
        this.textError = 'Email already used.'
      }
    },

    async register() {
      this.textError = ''
      const payload = {
        password: this.password,
        email: this.email,
        inviteCode: this.inviteCode,
        referralCode: this.referralCode,
        firstPage: utils.getCookie('ahaFirstPage'),
      }

      const arr = this.name.split(' ')

      if (arr.length === 1) {
        payload.firstName = this.name
      } else {
        [payload.firstName] = arr
        payload.lastName = this.name.replace(`${arr[0]} `, '')
      }

      if (!this.isValidSignUpForm()) {
        this.submitting = false
        return
      }

      const referrer = cookie.getValue('referrer')
      if (referrer) {
        payload.referrer = referrer
      }
      payload.source = window.location.href
      payload.tokenRecaptcha = this.tokenRecaptcha

      if (this.guestUserId) {
        payload.isGuest = false
        payload.newPassword = payload.password
        try {
          const res = await UserApi.patchUserById(this.guestUserId, payload)
          this.handleRegisterSuccess(res, payload, true)
        } catch (err) {
          this.handleRegisterError(err)
        }
        return
      }

      // eslint-disable-next-line promise/catch-or-return
      this.$http.post(configs.api.createAccount, payload).then(
        (res) => this.handleRegisterSuccess(res, payload),
        this.handleRegisterError,
      )
    },

    registerUser() {
      this.$router.push('/pages/register')
    },

    handleLoginWithSso() {
      utils.gaTrackEvent(
        PRESENTER_REGISTER,
        'Click Sign up button',
        'SSO',
      )
      this.$router.push('/pages/sso')
    },

    detectAndSetPageLanguage() {
      const userLang = navigator.language ? navigator.language : 'en'
      // eslint-disable-next-line prefer-destructuring
      this.signUpLanguage = userLang.split(/-|_/)[0]
      const signUpLang = ['ja', 'es']
      if (!signUpLang.includes(this.signUpLanguage)) return
      this.$store.dispatch('setLang', this.signUpLanguage)
    },
  },
}
</script>

<style lang="scss" scoped>
.logo img {
  max-width: 180px;
  margin-bottom: 25px;
}

.separator {
    display: flex;
    align-items: center;
    text-align: center;
    color: #4A4A4A;
}
.separator::before, .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #EBEBEB;
}
.separator::before {
    margin-right: 1.5em;
}
.separator::after {
    margin-left: 1.5em;
}

.separator {
  @apply text-base-90;
  display: flex;
  align-items: center;
  text-align: center;
}

.separator::before,
.separator::after {
  @apply border-base-30 border-b;
  content: '';
  flex: 1;
}

.separator:not(:empty)::before {
  @apply mr-5;
}

.separator:not(:empty)::after {
  @apply ml-5;
}
</style>
